<script setup>
import {computed, defineProps, onBeforeUnmount, onMounted, ref} from "vue";
import {useSettingsStore} from "@/store/settings";
import {adminSections, superadminSections} from "@/components/AdminView/adminSections";

const props = defineProps({
  mobile: {
    type: Boolean,
    default: false,
  },
});

const settingsStore = useSettingsStore();
const rootRef = ref(null);

const isAdmin = computed(() => settingsStore.hasAnyRole(['admin', 'superadmin']));
const isSuperadmin = computed(() => settingsStore.hasRole('superadmin'));

const profileItems = [
  {label: 'Контакты', routeName: 'ContactsView'},
  {label: 'Покупки', routeName: 'OrdersView'},
  {label: 'Монеты', routeName: 'AcquiredCoinsView'},
  {label: 'Заявка в клуб', routeName: 'ClubSignupStatusView'},
];

const adminOpen = ref(false);
const superAdminOpen = ref(false);
const profileOpen = ref(false);

function scrollToFooter() {
  const footer = document.getElementById('footer-contacts');
  if (footer) {
    footer.scrollIntoView({behavior: 'smooth'});
  }
}

function toggle(menu) {
  if (menu === 'superadmin') {
    superAdminOpen.value = !superAdminOpen.value;
    adminOpen.value = false;
    profileOpen.value = false;
  } else if (menu === 'admin') {
    adminOpen.value = !adminOpen.value;
    superAdminOpen.value = false;
    profileOpen.value = false;
  } else {
    profileOpen.value = !profileOpen.value;
    superAdminOpen.value = false;
    adminOpen.value = false;
  }
}

function closeMenus() {
  adminOpen.value = false;
  superAdminOpen.value = false;
  profileOpen.value = false;
}

function onDocumentClick(event) {
  if (props.mobile) {
    return;
  }
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    closeMenus();
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>

<template>
  <nav ref="rootRef" class="menuNav" :class="{ 'menuNav--mobile': mobile }">
    <ul class="menuNav__buttonList">
      <li class="menuNav__button">
        <a href='#/club' class="menuNav__link">Клуб</a>
      </li>
      <li class="menuNav__button">
        <a href='#/catalog' class="menuNav__link">Магазин</a>
      </li>
<!--
      TODO: Убрать о нас, так как это то же, что и клуб

      <li class="menuNav__button">
        <a class="menuNav__link">О нас</a>
      </li>
-->
      <li class="menuNav__button">
        <a class="menuNav__link" @click="scrollToFooter">Контакты</a>
      </li>

      <li
          v-if="isSuperadmin"
          class="menuNav__button menuNav__button--hasSubmenu"
          :class="{ 'menuNav__button--open': superAdminOpen }"
      >
        <a
            href="#/admin"
            class="menuNav__link"
            @click.prevent="toggle('superadmin')"
        >
          СуперАд
          <span class="menuNav__arrow"/>
        </a>
        <ul class="menuNav__submenu">
          <li v-for="section in superadminSections" :key="section.key">
            <router-link
                :to="{name: section.routeName}"
                class="menuNav__submenuLink"
            >
              {{ section.label }}
            </router-link>
          </li>
        </ul>
      </li>

      <li
          v-if="isAdmin"
          class="menuNav__button menuNav__button--hasSubmenu"
          :class="{ 'menuNav__button--open': adminOpen }"
      >
        <a
            href="#/admin"
            class="menuNav__link"
            @click.prevent="toggle('admin')"
        >
          Админка
          <span class="menuNav__arrow"/>
        </a>
        <ul class="menuNav__submenu">
          <li v-for="section in adminSections" :key="section.key">
            <router-link
                :to="{name: section.routeName}"
                class="menuNav__submenuLink"
            >
              {{ section.label }}
            </router-link>
          </li>
        </ul>
      </li>

      <li
          class="menuNav__button menuNav__button--hasSubmenu"
          :class="{ 'menuNav__button--open': profileOpen }"
      >
        <a
            href="#/lk"
            class="menuNav__link"
            @click.prevent="toggle('profile')"
        >
          Профиль
          <span class="menuNav__arrow"/>
        </a>
        <ul class="menuNav__submenu">
          <li v-for="item in profileItems" :key="item.routeName">
            <router-link
                :to="{name: item.routeName}"
                class="menuNav__submenuLink"
            >
              {{ item.label }}
            </router-link>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
.menuNav {
  &__button {
    position: relative;
    white-space: nowrap;
  }
  &__buttonList {
    display: flex;
    list-style-type: none;
    gap: 1.5rem;
    font-size: 16px;
  }

  &__link {
    display: flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    color: var(--cd-text);
    font-family: var(--cd-default-font);
    font-weight: var(--cd-default-font-weight);
    font-size: 16px;
    transition: colors 0.3s;
    text-decoration: none;
    cursor: pointer;
    &:hover {
      color: var(--cd-highlight-text);
    }
  }

  &__arrow {
    width: 7px;
    height: 7px;
    margin-left: 6px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg) translateY(-2px);
  }

  &__submenu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    display: none;
    flex-direction: column;
    gap: 4px;
    min-width: 220px;
    margin: 0;
    padding: 8px;
    list-style: none;
    text-align: left;
    background-color: var(--cd-card-bg);
    border: 1px solid var(--cd-spoiler-border-color);
    border-radius: 12px;
    box-shadow: 0 6px 30px #0000001a;
    z-index: 100;
  }

  &__submenuLink {
    display: block;
    padding: 8px 12px;
    border-radius: 8px;
    color: var(--cd-text-muted);
    text-decoration: none;
    transition: color 0.3s, background-color 0.3s;

    &:hover {
      color: var(--cd-highlight-text);
      background-color: var(--cd-button-bg-color);
    }

    &.router-link-active {
      color: var(--cd-highlight-text);
      background-color: var(--cd-button-bg-color);
    }
  }

  &--mobile {
    .menuNav__buttonList {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }

    .menuNav__button--hasSubmenu {
      display: contents;
    }

    .menuNav__link {
      height: auto;
      padding: 8px 14px;
      border: 1px solid transparent;
      border-radius: 1000px;
      background-color: var(--cd-button-bg-color);
      font-size: 14px;
      white-space: nowrap;
    }

    .menuNav__button--open .menuNav__link {
      border-color: var(--cd-base-green);
    }

    .menuNav__submenu {
      position: static;
      flex-basis: 100%;
      min-width: 0;
      max-width: 100%;
      margin: 0;
      padding: 0;
      background: transparent;
      border: none;
      box-shadow: none;
    }

    .menuNav__submenuLink {
      padding: 6px 12px;
      border: 1px solid var(--cd-spoiler-border-color);
      border-radius: 1000px;
      background-color: var(--cd-card-bg);
      font-size: 14px;
      white-space: nowrap;
    }

    .menuNav__submenuLink.router-link-active {
      border-color: var(--cd-base-green);
      color: var(--cd-text);
    }
  }
}

.menuNav__button--open > .menuNav__submenu {
  display: flex;
}

// Desktop: прозрачный мостик через зазор, чтобы курсор доходил до подменю
.menuNav:not(.menuNav--mobile) .menuNav__button--hasSubmenu::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 8px;
}

// Desktop: подменю по наведению и фокусу
.menuNav:not(.menuNav--mobile) .menuNav__button--hasSubmenu:hover > .menuNav__submenu,
.menuNav:not(.menuNav--mobile) .menuNav__button--hasSubmenu:focus-within > .menuNav__submenu {
  display: flex;
}
</style>
